import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ExternalLink, X } from "lucide-react";
import { DENETIM_TURLERI, type DenetimAyrintisi, type DenetimListesi, type DenetimSatiri, type DenetimSayimi, type DenetimTuru, type DenetimUyarisi } from "@oasis/domain";
import { Band, Buton, Etiket, Hata, alan } from "@/components/parcalar";
import { AltBaslik } from "@/components/belge-okuma-bolumu";
import { UyariCipi, uyariKoduRengi, uyariKutusuTonu } from "@/components/belge-karti";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";
import { goruntuleyiciLinki } from "@/lib/goruntuleyici";
import { kalipMetni, yerCevir } from "@/lib/belgeler";
import {
  DENETIM_UCU,
  denetimListeYolu,
  denetimNotMetni,
  durumAdi,
  durumTonu,
  kosuMetni,
  sayimImzasi,
  sayimSatirlari,
  sonucMetni,
  sozlesmeMetni,
  tarihHatasi,
  turAdi,
} from "@/lib/belge-denetimi";
import { tarih, tarihSaat, useVeri } from "@/lib/veri";

/**
 * D-074 Faz 2 · PR 12 (K19): Documents › Audit (prototip belgeler--denetim). Dosyalanmış belgelerin denetimi: toplu sıra paneli (türler, dosyalanma tarih
 * aralığı; önce "Count" kuru koşusu, sonra "Queue {0} documents"), denetim listesi ve satırın ayrıntı kartı (okumanın kaynağı, karşılaştırma notları,
 * bulgular, "Mark seen" notla). Yalnız `belgeUyari` rolüne görünür (Belgeler.tsx). Bulgular hiçbir şeyi değiştirmez: dosya, kayıt, adım, kaynak tarihi ve
 * MRB aynen kalır; Needs me, zil ve Documents kartları bu bulguları görmez.
 */

/** Liste sütunları (Documents listesinin ızgarası; kapsayıcı 54rem altında etiketli kartlar). */
const SATIR_IZGARASI = "grid grid-cols-2 gap-x-3 gap-y-2 @min-[54rem]:grid-cols-[minmax(220px,2.2fr)_minmax(140px,1.1fr)_minmax(170px,1.4fr)_minmax(130px,1fr)_minmax(120px,.9fr)] @min-[54rem]:items-center @min-[54rem]:gap-3";
const HUCRE_ETIKETI = "mb-px block text-[11px] font-semibold uppercase tracking-[0.02em] text-ink-3 @min-[54rem]:hidden";
const ALT = "block text-xs font-normal text-ink-3 [overflow-wrap:anywhere]";
/** Sayım tablosu (tasarım `.bl-kars`): dört sütun. */
const SAYIM_IZGARASI = "grid grid-cols-[minmax(0,1.6fr)_70px_minmax(0,2fr)_70px] gap-3 px-2.5 py-1.5 text-sm";
const VARSAYILAN_TURLER: readonly DenetimTuru[] = ["hidrotest", "ndt"];

const hataOf = (e: unknown) => (e instanceof Error ? e.message : cevir("Error"));

/** "Queue for audit" paneli. `onSiraya`: gerçek koşudan sonra liste ve sekme sayısı tazelenir. */
function SiraPaneli({ onSiraya }: { onSiraya: () => Promise<void> | void }) {
  const [turler, setTurler] = useState<DenetimTuru[]>([...VARSAYILAN_TURLER]);
  const [bas, setBas] = useState("");
  const [son, setSon] = useState("");
  const [sayim, setSayim] = useState<{ s: DenetimSayimi; imza: string } | null>(null);
  const [eskidi, setEskidi] = useState(false);
  const [mesgul, setMesgul] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [sonuc, setSonuc] = useState<string | null>(null);
  const imza = sayimImzasi(turler, bas, son);
  // Seçim değişince sayım silinir ("Queue …" yeniden sayılmadan açılmaz).
  useEffect(() => {
    if (sayim && sayim.imza !== imza) { setSayim(null); setEskidi(true); }
  }, [imza, sayim]);

  const govde = (kuru: boolean) => ({ turler, bas: bas || null, son: son || null, kuru });
  const say = async () => {
    setHata(null);
    setSonuc(null);
    if (!turler.length) { setHata(cevir("Choose at least one document type.")); return; }
    const th = tarihHatasi(bas, son);
    if (th) { setHata(th); return; }
    setMesgul(true);
    try {
      setSayim({ s: await api.post<DenetimSayimi>(DENETIM_UCU.sira, govde(true)), imza });
      setEskidi(false);
    } catch (e) {
      setHata(hataOf(e));
    } finally {
      setMesgul(false);
    }
  };
  const sirayaKoy = async () => {
    if (!sayim || sayim.imza !== imza) return;
    setMesgul(true);
    setHata(null);
    try {
      const s = await api.post<DenetimSayimi>(DENETIM_UCU.sira, govde(false));
      setSonuc(sonucMetni(s));
      setSayim(null);
      await onSiraya();
    } catch (e) {
      setHata(hataOf(e));
    } finally {
      setMesgul(false);
    }
  };
  const degistir = (t: DenetimTuru, acik: boolean) => setTurler((l) => (acik ? DENETIM_TURLERI.filter((x) => x === t || l.includes(x)) : l.filter((x) => x !== t)));
  const alinacak = sayim?.s.toplam.alinacak ?? 0;

  return (
    <div className="mx-4 my-3 space-y-2.5 rounded-lg border border-warn! bg-surface p-3" data-bolum="Queue for audit">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1"><span className="text-md font-semibold">{cevir("Queue for audit")}</span><span className="text-xs text-ink-3">{cevir("Audit jobs wait behind every normal reading.")}</span></div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
        <span className="text-ink-2">{cevir("Document types")}</span>
        {DENETIM_TURLERI.map((t) => (
          <label key={t} className="flex items-center gap-1.5"><input type="checkbox" checked={turler.includes(t)} disabled={mesgul} onChange={(e) => degistir(t, e.target.checked)} /> {turAdi(t)}</label>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-x-3 gap-y-2 text-sm">
        <label className="flex flex-col gap-0.5"><span className="text-ink-2">{cevir("Filed from")}</span><input type="date" className={cn(alan, "w-[11rem]")} value={bas} disabled={mesgul} aria-label={cevir("Filed from")} onChange={(e) => setBas(e.target.value)} /></label>
        <label className="flex flex-col gap-0.5"><span className="text-ink-2">{cevir("to")}</span><input type="date" className={cn(alan, "w-[11rem]")} value={son} disabled={mesgul} aria-label={cevir("to")} onChange={(e) => setSon(e.target.value)} /></label>
        <Buton mesgul={mesgul} onClick={say}>{cevir("Count")}</Buton>
      </div>
      {eskidi && !sayim && <p className="text-xs text-ink-3">{cevir("Count again: the choice changed.")}</p>}
      {sayim && (
        <div data-bolum="Audit count">
          <div className="overflow-hidden rounded-md border border-line">
            <div className={cn(SAYIM_IZGARASI, "bg-surface-2 text-[11.5px] font-semibold uppercase tracking-[0.02em] text-ink-3")}>
              <span>{cevir("Type")}</span><span>{cevir("Found")}</span><span>{cevir("Not read again")}</span><span>{cevir("To read")}</span>
            </div>
            {sayimSatirlari(sayim.s, turler).map((r) => (
              <div key={r.ad} className={cn(SAYIM_IZGARASI, "border-t border-line [&>*]:min-w-0", r.toplam && "font-semibold")}>
                <span>{r.ad}</span><span className="tnum">{r.bulunan}</span><span className="[overflow-wrap:anywhere]">{r.okunmayan}</span><span className="tnum">{r.okunacak}</span>
              </div>
            ))}
          </div>
          {kosuMetni(sayim.s).map((m) => <p key={m} className="mt-1.5 text-xs text-ink-3">{m}</p>)}
          <p className="mt-1 text-xs text-ink-3">{cevir("The scheduled reader does not take these types yet; the jobs wait in the queue.")}</p>
        </div>
      )}
      <Hata mesaj={hata} />
      {sonuc && <p className="text-sm text-ok">{sonuc}</p>}
      <div className="flex flex-wrap gap-2">
        <Buton tur="birincil" islem="belgeUyari" mesgul={mesgul} disabled={!sayim || sayim.imza !== imza || alinacak <= 0} onClick={sirayaKoy}>{cevir("Queue {0} documents", alinacak)}</Buton>
        {sayim && <Buton disabled={mesgul} onClick={() => setSayim(null)}>{cevir("Cancel")}</Buton>}
      </div>
    </div>
  );
}

/** Listenin satırı (dar ekranda etiketli kart). */
function DenetimSatirGorunumu({ s, acik, onAc }: { s: DenetimSatiri; acik: boolean; onAc: () => void }) {
  const alt = s.durum === "okundu" ? tarihSaat(s.okumaTs) : s.durum === "hata" ? s.hata : s.durum === "bekliyor" && s.kaynakKod ? cevir("{0} is in the queue.", s.kaynakKod) : s.durum === "okunmadi" ? cevir("Run the audit again to retry.") : null;
  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={acik}
      onClick={onAc}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onAc(); } }}
      className={cn(SATIR_IZGARASI, "cursor-pointer border-t border-line px-4 py-2.5 text-base hover:bg-surface-2/60 [&>*]:min-w-0", acik && "bg-surface-2/60")}
    >
      <div className="col-span-2 @min-[54rem]:col-span-1">
        <span className="font-semibold [overflow-wrap:anywhere]">{s.dosya}</span>
        <span className={ALT}>{s.kod} · {cevir("filed {0}", tarih(s.dosyalamaTs))}</span>
      </div>
      <div><span className={HUCRE_ETIKETI}>{cevir("Type")}</span>{turAdi(s.tur)}</div>
      <div><span className={HUCRE_ETIKETI}>{cevir("Place")}</span>{yerCevir(s.yerMetni)}</div>
      <div>
        <span className={HUCRE_ETIKETI}>{cevir("Status")}</span>
        <Etiket tone={durumTonu(s.durum)}>{durumAdi(s.durum)}</Etiket>
        {alt && <span className={ALT}>{alt}</span>}
      </div>
      <div>
        <span className={HUCRE_ETIKETI}>{cevir("Findings")}</span>
        {s.acikUyarilar.length ? <span className="flex flex-wrap gap-1">{s.acikUyarilar.map((u) => <UyariCipi key={u.kod} kod={u.kod} renk={u.renk} />)}</span> : s.durum === "okundu" ? <span className="text-ink-3">{s.gorulenBulgu > 0 ? cevir("all seen") : cevir("none")}</span> : <span className="text-ink-3">—</span>}
      </div>
    </div>
  );
}

/** Tek bulgu: kod, cümle, sayfa bağlantıları, "Seen by …". */
function BulguSatiri({ u, secili, onSec }: { u: DenetimUyarisi; secili: boolean; onSec: () => void }) {
  return (
    <li className={cn("rounded-md border-l-4 px-2.5 py-2 text-sm", u.karar ? uyariKutusuTonu("gri") : uyariKutusuTonu(u.renk), secili && "ring-1 ring-line-strong")} data-uyari={u.kod}>
      <p>
        <span className={cn("mr-1.5 font-mono text-xs font-semibold", u.karar ? "text-ink-3" : uyariKoduRengi(u.renk))}>{u.kod}</span>
        {kalipMetni(u.kalip, u.degerler)}
        {u.sayfalar.map((x) => <span key={`${x.dokumanId}-${x.sayfa}`}> <a className="text-xs text-orange underline" href={goruntuleyiciLinki(x.dokumanId, { sayfa: x.sayfa })} target="_blank" rel="noreferrer">p.{x.sayfa}</a></span>)}
      </p>
      {u.karar && <p className="mt-1 text-xs text-ink-2">{cevir("Seen by {0} on {1}: {2}", u.karar.kim, tarih(u.karar.ts), u.karar.not ?? "")}</p>}
      {!u.karar && u.gorulebilir && !secili && <Buton kucuk className="mt-1.5" onClick={onSec}>{cevir("Mark seen")}</Buton>}
    </li>
  );
}

/** Satırın ayrıntı kartı: okuma, notlar, bulgular, "Mark seen", sıradan çıkarma. */
export function DenetimKarti({ id, onKapat, onDegisti }: { id: string; onKapat: () => void; onDegisti: () => Promise<void> | void }) {
  const { veri, hata, yenile } = useVeri<DenetimAyrintisi>(DENETIM_UCU.ayrinti(id));
  const [secilen, setSecilen] = useState<string | null>(null);
  const [not, setNot] = useState("");
  const [mesgul, setMesgul] = useState(false);
  const [islemHatasi, setIslemHatasi] = useState<string | null>(null);
  const gorulebilir = useMemo(() => (veri?.uyarilar ?? []).filter((u) => !u.karar && u.gorulebilir), [veri]);
  /** Seçili bulgu: kişinin seçtiği, yoksa ilk görülebilir bulgu. */
  const secili = gorulebilir.find((u) => u.id === secilen) ?? gorulebilir[0] ?? null;

  if (hata) return <Hata mesaj={hata} />;
  if (!veri) return <p className="text-sm text-ink-3">…</p>;
  const s = veri.satir;
  const calistir = async (f: () => Promise<unknown>) => {
    setMesgul(true);
    setIslemHatasi(null);
    try {
      await f();
      await Promise.all([yenile(), onDegisti()]);
      return true;
    } catch (e) {
      setIslemHatasi(hataOf(e));
      return false;
    } finally {
      setMesgul(false);
    }
  };
  const gorulduYap = async () => {
    if (!secili) return;
    if (not.trim().length < 3) { setIslemHatasi(cevir("Write the reason.")); return; }
    if (await calistir(() => api.post(DENETIM_UCU.goruldu(secili.id), { not: not.trim() }))) { setNot(""); setSecilen(null); }
  };

  return (
    <article className="space-y-3 rounded-lg border border-line bg-surface p-3" data-bolum="Audit findings">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <div className="min-w-0 flex-[1_1_260px]">
          <div className="font-semibold [overflow-wrap:anywhere]">{s.dosya}</div>
          <div className={ALT}>{s.kod} · {cevir("filed {0}", tarih(s.dosyalamaTs))} · {yerCevir(s.yerMetni)}</div>
        </div>
        <Etiket tone={durumTonu(s.durum)}>{durumAdi(s.durum)}</Etiket>
        <a className="inline-flex h-kontrol-kucuk items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 text-sm font-medium text-ink hover:border-line-strong" href={goruntuleyiciLinki(s.dokumanId)} target="_blank" rel="noreferrer"><ExternalLink size={13} />{cevir("Open file")}</a>
        {(s.durum === "sirada" || s.durum === "hata") && <Buton kucuk disabled={mesgul} onClick={() => void calistir(() => api.post(DENETIM_UCU.cikar(s.id)))}>{cevir("Remove from audit queue")}</Buton>}
        <button type="button" onClick={onKapat} className="shrink-0 rounded p-0.5 hover:bg-surface-2" aria-label={cevir("Close")}><X size={14} /></button>
      </div>
      <div className="grid gap-4 @min-[54rem]:grid-cols-2">
        <div>
          <AltBaslik>{cevir("How it was read")}</AltBaslik>
          {veri.okuma ? (
            <ul className="space-y-0.5 text-sm">
              <li>{veri.okuma.kaynak === "kopya" ? cevir("Read once: same file as {0}.", veri.okuma.kaynakKod ?? "—") : cevir("Read by the converter on {0}", tarihSaat(veri.okuma.ts))}</li>
              {veri.karsilastirmaTs && <li>{cevir("Compared with OASIS on {0}, for the place it is filed at", tarihSaat(veri.karsilastirmaTs))}</li>}
              <li><span className="text-ink-3">{cevir("Contract")}:</span> {sozlesmeMetni(s.tur)}</li>
            </ul>
          ) : (
            <p className="text-sm text-ink-2">{s.durum === "hata" && s.hata ? s.hata : durumAdi(s.durum)}</p>
          )}
          {veri.notlar.length > 0 && (
            <>
              <AltBaslik>{cevir("Notes")}</AltBaslik>
              <ul className="list-disc space-y-0.5 pl-4 text-sm text-ink-2">{veri.notlar.map((n, i) => <li key={i}>{denetimNotMetni(s.tur, n)}</li>)}</ul>
            </>
          )}
          {veri.gecmis.length > 0 && (
            <>
              <AltBaslik>{cevir("History")}</AltBaslik>
              <ul className="space-y-0.5 text-xs text-ink-2">{veri.gecmis.map((g, i) => <li key={i}><span className="tnum text-ink-3">{tarihSaat(g.ts)}</span> · {g.kim} · {g.olay}</li>)}</ul>
            </>
          )}
        </div>
        <div>
          <AltBaslik>{cevir("Findings")}</AltBaslik>
          {veri.uyarilar.length ? (
            <ul className="space-y-1.5">{veri.uyarilar.map((u) => <BulguSatiri key={u.id} u={u} secili={secili?.id === u.id} onSec={() => setSecilen(u.id)} />)}</ul>
          ) : (
            <p className="text-sm text-ink-2">{veri.okuma ? cevir("No audit findings.") : "—"}</p>
          )}
          {secili && (
            <div className="mt-2 space-y-2 rounded-lg border border-line-strong! bg-surface p-2.5" data-bolum="Mark seen">
              <p className="text-sm font-semibold">{secili.kod} · {cevir("Quality Lead or PM")}</p>
              <textarea rows={2} className={alan} value={not} disabled={mesgul} placeholder={cevir("Note (required)")} aria-label={cevir("Note (required)")} onChange={(e) => setNot(e.target.value)} />
              <Buton kucuk tur="birincil" islem="belgeUyari" mesgul={mesgul} onClick={gorulduYap}>{cevir("Mark seen")}</Buton>
            </div>
          )}
          <Hata mesaj={islemHatasi} />
          <div className="mt-2 rounded-lg bg-surface-2 p-2.5 text-sm">
            <b>{cevir("Audit findings change nothing")}</b>
            <ul className="mt-1 list-disc space-y-0.5 pl-4 text-ink-2">
              <li>{cevir("The file stays where it is filed; no record, step, weld date or MRB changes.")}</li>
              <li>{cevir("Correct a record on its own screen if needed.")}</li>
            </ul>
          </div>
        </div>
      </div>
    </article>
  );
}

/**
 * Documents › Audit görünümü. `ust`: Documents'ın süzgeç satırı ve "Audit" sekmesi (Belgeler.tsx); `onDegisti`: sekmenin sayısını tazeler.
 */
export function DenetimGorunumu({ ust, onDegisti }: { ust: ReactNode; onDegisti?: () => Promise<void> | void }) {
  const [ara, setAra] = useState("");
  const [aranan, setAranan] = useState("");
  const [acik, setAcik] = useState(false);
  useEffect(() => {
    const z = setTimeout(() => setAranan(ara), 300);
    return () => clearTimeout(z);
  }, [ara]);
  const { veri, hata, yenile } = useVeri<DenetimListesi>(denetimListeYolu({ acik, ara: aranan }));
  const [ekler, setEkler] = useState<DenetimSatiri[]>([]);
  const [sonraki, setSonraki] = useState<string | null>(null);
  const [acikId, setAcikId] = useState<string | null>(null);
  useEffect(() => {
    setEkler([]);
    setSonraki(veri?.sonraki ?? null);
  }, [veri]);
  const satirlar = useMemo(() => [...(veri?.satirlar ?? []), ...ekler], [veri, ekler]);
  const dahaFazla = async () => {
    if (!sonraki) return;
    const y = await api.get<DenetimListesi>(denetimListeYolu({ acik, ara: aranan, once: sonraki }));
    setEkler((e) => [...e, ...y.satirlar]);
    setSonraki(y.sonraki);
  };
  const degisti = async () => { await Promise.all([yenile(), onDegisti?.()]); };

  return (
    <div data-bolum="Audit">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line px-4 py-3">
        <span className="text-md font-semibold">{cevir("Audit of filed documents")}</span>
        {ust}
        <input className="h-[34px] min-w-0 max-w-[380px] flex-[1_1_220px] rounded-lg border border-line-strong bg-bg px-2.5 text-base text-ink outline-none placeholder:text-ink-3 focus:border-orange" placeholder={cevir("Search file, riser, SN or code...")} aria-label={cevir("Search file, riser, SN or code...")} value={ara} onChange={(e) => setAra(e.target.value)} />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={acik} onChange={(e) => setAcik(e.target.checked)} /> {cevir("Open findings only")}</label>
      </div>
      <p className="px-4 pt-3 text-base leading-relaxed text-ink-2">{cevir("Filed hydrotest certificates, NDT reports, weld maps and PWHT charts are read again and compared with OASIS. Only findings are shown: no record, step or file changes.")}</p>
      <SiraPaneli onSiraya={degisti} />
      <Hata mesaj={hata} />
      {veri && !satirlar.length ? (
        <p className="border-t border-line px-4 py-3.5 text-base text-ink-2">{cevir("No documents in the audit yet.")}</p>
      ) : (
        <div className="@container" data-bolum="Audit list">
          <div className={cn(SATIR_IZGARASI, "hidden bg-surface-2 px-4 py-2 text-[11.5px] font-semibold uppercase tracking-[0.02em] text-ink-3 @min-[54rem]:grid")}>
            <span>{cevir("File")}</span><span>{cevir("Type")}</span><span>{cevir("Place")}</span><span>{cevir("Status")}</span><span>{cevir("Findings")}</span>
          </div>
          {satirlar.map((s) => (
            <div key={s.id}>
              <DenetimSatirGorunumu s={s} acik={acikId === s.id} onAc={() => setAcikId(acikId === s.id ? null : s.id)} />
              {acikId === s.id && <div className="border-t border-line bg-surface-2/40 p-3"><DenetimKarti id={s.id} onKapat={() => setAcikId(null)} onDegisti={degisti} /></div>}
            </div>
          ))}
          {sonraki && <div className="border-t border-line px-4 py-2.5"><Buton kucuk onClick={dahaFazla}>{cevir("Show more")}</Buton></div>}
        </div>
      )}
      <Band tip="bilgi">{cevir("Audit findings change nothing. Correct a record on its own screen if needed.")}</Band>
    </div>
  );
}
